/* Real backend client. Mirrors the base44 SDK surface used across the app,
   but calls the FastAPI backend. LLM/file integrations remain mocked client-side. */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true });

const ENTITY_NAMES = [
  "User", "BusinessCustomer", "AcquisitionCandidate", "AcquisitionActivity",
  "Investor", "Investment", "InvestmentBudget", "FollowUp", "Project", "Phase",
  "Milestone", "ProjectExpense", "Task", "TaskTemplate", "Team", "TeamMember",
  "TimeEntry", "Invoice", "ScorecardTemplate", "Activity", "Comment", "Document",
];

function makeEntity(name) {
  return {
    async list(sort, limit) {
      const { data } = await api.get(`/entities/${name}`, { params: { sort, limit } });
      return data;
    },
    async filter(query, sort, limit) {
      const { data } = await api.post(`/entities/${name}/query`, { query, sort, limit });
      return data;
    },
    async get(id) {
      const { data } = await api.get(`/entities/${name}/${id}`);
      return data;
    },
    async create(payload) {
      const { data } = await api.post(`/entities/${name}`, payload);
      return data;
    },
    async bulkCreate(items) {
      const { data } = await api.post(`/entities/${name}/bulk`, { items });
      return data;
    },
    async update(id, payload) {
      const { data } = await api.put(`/entities/${name}/${id}`, payload);
      return data;
    },
    async delete(id) {
      const { data } = await api.delete(`/entities/${name}/${id}`);
      return data;
    },
  };
}

const entities = {};
ENTITY_NAMES.forEach((n) => { entities[n] = makeEntity(n); });
entities.Query = { async list() { return []; }, async filter() { return []; } };

const auth = {
  async me() {
    const { data } = await api.get("/auth/me");
    return data;
  },
  async updateMe(payload) {
    const { data } = await api.put("/auth/me", payload);
    return data;
  },
  async logout() {
    const { data } = await api.post("/auth/logout");
    return data;
  },
  async createSession(session_id) {
    const { data } = await api.post("/auth/session", { session_id });
    return data;
  },
  redirectToLogin() {
    window.location.href = "/login";
  },
};

/* ---------------- Integrations (still MOCKED client-side) ---------------- */
const delay = (ms = 400) => new Promise((r) => setTimeout(r, ms));
function genFromSchema(schema) {
  if (!schema || typeof schema !== "object") return "Mock-innsikt basert p\u00e5 tilgjengelige data.";
  const t = schema.type;
  if (t === "object") {
    const o = {};
    Object.entries(schema.properties || {}).forEach(([k, v]) => { o[k] = genFromSchema(v); });
    return o;
  }
  if (t === "array") {
    const item = schema.items || { type: "string" };
    return [genFromSchema(item), genFromSchema(item)];
  }
  if (t === "number" || t === "integer") return Math.round(Math.random() * 60 + 40);
  if (t === "boolean") return true;
  if (schema.enum) return schema.enum[0];
  return "Mock-analyse (koble til en ekte AI-modell for reelle resultater).";
}
const Core = {
  async InvokeLLM({ prompt, response_json_schema } = {}) {
    try {
      const { data } = await api.post("/integrations/llm", { prompt, response_json_schema });
      return data.result;
    } catch (e) {
      if (response_json_schema) return genFromSchema(response_json_schema);
      return "Kunne ikke hente AI-analyse akkurat nå.";
    }
  },
  async SendEmail() { await delay(300); return { success: true }; },
  async SendSMS() { await delay(300); return { success: true }; },
  async UploadFile({ file } = {}) {
    await delay(400);
    let file_url = "https://via.placeholder.com/400x300?text=Mock+fil";
    try { if (file) file_url = URL.createObjectURL(file); } catch (e) { /* ignore */ }
    return { file_url };
  },
  async GenerateImage() { await delay(500); return { url: "https://via.placeholder.com/512?text=Mock+bilde" }; },
  async ExtractDataFromUploadedFile({ json_schema } = {}) {
    await delay(500);
    const output = json_schema ? genFromSchema(json_schema) : [];
    return { status: "success", output, details: "Mock-uttrekk" };
  },
};

export const base44 = { entities, auth, integrations: { Core }, appLogs: { async logUserInApp() { return { success: true }; } } };
export const apiClient = api;
